import React, { useState } from "react";
import { Header } from "../../../../../components";
import { Button, message, Alert } from "antd";
import { UseFetchCreateInputAudit } from "../hooks/useFetch";
import Table from "../components/table";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Excel เก็บวันเป็นจำนวนวันนับจาก 1899-12-30 → แปลงเป็น epoch ต้องลบ 25569
const EXCEL_EPOCH_OFFSET = 25569;

const parseAuditDate = (cell) => {
  if (cell === null || cell === undefined || cell === "") return "";
  const parsed =
    typeof cell === "number"
      ? dayjs.utc((cell - EXCEL_EPOCH_OFFSET) * 86400 * 1000)
      : dayjs(cell);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "";
};

const isEmptyRow = (row) =>
  !row ||
  row.every((cell) => cell === null || cell === undefined || cell === "");

function InputAuditPage() {
  const titlePage = "Masterdata  Audit Input";
  const [dataInputs, setDataInputs] = useState([]);
  const [fileInfo, setFileInfo] = useState(null);
  const { mutateAsync: createInputAudit, isLoading: isCreating } =
    UseFetchCreateInputAudit();

  const handleFileUpload = (event) => {
    const fileInput = event.target;
    const file = fileInput.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      fileInput.value = "";
      const { rows, skippedNoNumber, invalidDateRows, dates } =
        formatData(jsonData);
      setDataInputs(rows);
      setFileInfo({ skippedNoNumber, invalidDateRows, dates });
    };
    reader.readAsArrayBuffer(file);
  };

  const formatData = (jsonData) => {
    const rows = [];
    let skippedNoNumber = 0;
    const invalidDateRows = [];
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (isEmptyRow(row)) continue;
      const numberFromSheet = parseInt(row[0]);
      // เดิมเป็น break — ช่องลำดับว่างแถวเดียวคือหยุดอ่านทั้งไฟล์แบบไม่บอกใคร
      // ที่เหลือของไฟล์หายไปเงียบ ๆ แล้วไม่มีใครรู้ว่ารายการไหนไม่ได้ถูกตรวจ
      if (!Number.isFinite(numberFromSheet)) {
        skippedNoNumber += 1;
        continue;
      }
      const dateAudit = parseAuditDate(row[7]);
      // วัน-เวลาเป็นส่วนหนึ่งของกุญแจกันนำเข้าซ้ำ ถ้าว่างต้องหยุดไว้ก่อนส่ง
      if (!dateAudit) invalidDateRows.push(numberFromSheet);
      rows.push({
        No: numberFromSheet,
        bank: row[1],
        username: row[2],
        amount: row[3],
        amountRemain: row[6],
        dateAudit,
        remark: row[8],
        numberFromSheet,
      });
    }
    const dates = [
      ...new Set(rows.filter((r) => r.dateAudit).map((r) => r.dateAudit.slice(0, 10))),
    ].sort();
    return { rows, skippedNoNumber, invalidDateRows, dates };
  };

  const resetFile = () => {
    setDataInputs([]);
    setFileInfo(null);
  };

  const hasInvalidDate = (fileInfo?.invalidDateRows?.length || 0) > 0;
  const canSubmit = dataInputs.length > 0 && !isCreating && !hasInvalidDate;

  const handleCreateInput = async () => {
    try {
      // ไม่ส่งวันจาก DatePicker อีกแล้ว — ฝั่ง API อ่านวันจากคอลัมน์วัน-เวลาในชีตเอง
      // เดิมสองค่านี้ไม่ตรงกันได้ ทำให้ตัวกันนำเข้าซ้ำไปดูข้อมูลของอีกวัน แล้วลงซ้ำทั้งไฟล์
      const res = await createInputAudit({ data: dataInputs });
      message.success(res?.message || "Create Input Audit Success..");
      resetFile();
    } catch (error) {
      message.error(error.response?.data?.message || "Error");
    }
  };

  return (
    <div className="md:m-10">
      <div className="flex justify-between">
        <Header category="App" title={titlePage} />
        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
          <Button
            type="primary"
            style={{ backgroundColor: "#1677ff", color: "#fff" }}
            onClick={() => document.getElementById("input-file-upload").click()}
          >
            เพิ่มไฟล์
          </Button>
          <Button
            type="primary"
            style={{
              backgroundColor: canSubmit ? "#47d604" : "#ccc",
              color: "#fff",
            }}
            onClick={() => handleCreateInput()}
            loading={isCreating}
            disabled={!canSubmit}
          >
            ส่งข้อมูล
          </Button>
          <Button
            type="primary"
            style={{
              backgroundColor: dataInputs.length === 0 ? "#ccc" : "#ff4d4f",
              color: "#fff",
            }}
            onClick={resetFile}
            disabled={dataInputs.length === 0}
          >
            ล้างข้อมูล
          </Button>
        </div>
        <input
          id="input-file-upload"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </div>

      {fileInfo && (
        <div style={{ marginBottom: 12, display: "grid", gap: 8 }}>
          <Alert
            type="info"
            showIcon
            message={
              `อ่านได้ ${dataInputs.length} แถว` +
              (fileInfo.dates.length > 0
                ? ` · ไฟล์นี้ครอบวันที่ ${fileInfo.dates.join(", ")}`
                : "")
            }
            description={
              fileInfo.skippedNoNumber > 0
                ? `ข้าม ${fileInfo.skippedNoNumber} แถวเพราะไม่มีเลขลำดับในคอลัมน์แรก`
                : undefined
            }
          />
          {hasInvalidDate && (
            <Alert
              type="error"
              showIcon
              message={`มี ${fileInfo.invalidDateRows.length} แถวที่วัน-เวลา (คอลัมน์ H) อ่านไม่ออก — ส่งข้อมูลไม่ได้จนกว่าจะแก้ไฟล์`}
              description={`แถวที่ ${fileInfo.invalidDateRows.slice(0, 20).join(", ")}${
                fileInfo.invalidDateRows.length > 20 ? " ..." : ""
              }`}
            />
          )}
        </div>
      )}

      <Table data={dataInputs} bordered loading={isCreating} />
    </div>
  );
}

export default InputAuditPage;
